interface ChartDataPoint {
  label: string
  values: {
    value: number
  }
}

interface MergeAnalyticsDataParams {
  allOptions: string[]
  categories?: string[]
  series?: number[]
}

interface Option {
  label: string
  value: string
}

/**
 * Merges backend analytics data with all available options, filling missing values with 0
 * Ensures that all options are represented in the chart data even if they have no responses
 */
export function mergeAnalyticsData({
  allOptions,
  categories,
  series,
}: MergeAnalyticsDataParams): ChartDataPoint[] {
  // Create a map of category to series value for quick lookup
  const categoryToValueMap = new Map<string, number>()
  if (categories && series) {
    categories.forEach((category, index) => {
      categoryToValueMap.set(category, series[index] || 0)
    })
  }

  // Create merged data with all options, filling missing values with 0
  return allOptions.map((option) => ({
    label: option,
    values: {
      value: categoryToValueMap.get(option) || 0,
    },
  }))
}

/**
 * Maps chart data labels to option labels using the element options array
 * Useful for rating components where numeric values need to be mapped to descriptive labels
 */
export function mapChartDataToOptionLabels(
  chartData: ChartDataPoint[],
  options: Option[]
): ChartDataPoint[] {
  return chartData.map((dataPoint) => {
    // Find the matching option by value
    const matchingOption = options.find((option) => option.value === dataPoint.label)

    return {
      ...dataPoint,
      label: matchingOption?.label || dataPoint.label,
    }
  })
}
